// src/pages/AgenticWorkflow/AgenticWorkflow.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from 'common/components';
import { useTenantScope } from 'hooks';
import NotificationModal from 'components/NotificationModal';
import { UploadContactsModal } from 'pages/AgenticWorkflowDetail/components/UploadContactsModal';
import { useGetAgenticWorkflows } from 'services/hooks/queries';
import { Workflow } from './AgenticWorkflow.types';
import WorkflowsTable from './components/WorkflowsTable';
import { NotificationModalState } from 'pages/AgenticWorkflowDetail/AgenticWorkflowDetail.types';

import styles from './AgenticWorkflow.module.scss';

const AgenticWorkflow: React.FC = () => {
    const navigate = useNavigate();
    const { isTenantScopeRequired } = useTenantScope();

    const {
        agenticWorkflows: workflows,
        isLoadingAgenticWorkflows: loading,
        refetchAgenticWorkflows,
        isError,
    } = useGetAgenticWorkflows();

    // State for Upload Modal
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
    const [currentWorkflowForUpload, setCurrentWorkflowForUpload] = useState<Workflow | null>(null);

    // State for notification modal
    const [notification, setNotification] = useState<NotificationModalState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'success',
        buttons: [],
        body: undefined,
    });

    useEffect(() => {
        if (isError) {
            setNotification({
                isOpen: true,
                title: 'Error',
                message: 'Failed to fetch workflows. Please try again.',
                type: 'error',
            });
        }
    }, [isError]);

    // Navigate to workflow builder
    const handleCreateWorkflowClick = (): void => {
        if (isTenantScopeRequired) return;
        navigate('/agentic-workflow-builder/new');
    };

    // Navigate to workflow detail page
    const handleRowClick = (workflow: Workflow): void => {
        navigate(`/agentic-workflow/${workflow.id}`, {
            state: { workflow },
        });
    };

    // Upload Modal handlers
    const openUploadModal = (workflow: Workflow): void => {
        if (isTenantScopeRequired) return;
        setCurrentWorkflowForUpload(workflow);
        setIsUploadModalOpen(true);
    };

    const closeUploadModal = (): void => {
        setIsUploadModalOpen(false);
        setCurrentWorkflowForUpload(null);
    };

    const handleUploadNotification = (notif: NotificationModalState): void => {
        setNotification(notif);
    };

    const closeNotification = (): void => {
        setNotification((prev) => ({ ...prev, isOpen: false }));
    };

    // Get status badge class
    const getStatusClass = (status?: string): string => {
        const statusLower = (status || '').toLowerCase();
        switch (statusLower) {
            case 'active':
                return styles.active;
            case 'paused':
                return styles.paused;
            case 'draft':
                return styles.draft;
            case 'completed':
                return styles.completed;
            default:
                return '';
        }
    };

    // Format date for display
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <h1 className={styles.title}>Agentic Workflows</h1>
                <Button
                    variant='primary'
                    icon={Plus}
                    onClick={handleCreateWorkflowClick}
                    disabled={isTenantScopeRequired}
                >
                    Create Workflow
                </Button>
            </div>

            {/* Workflow Table */}
            <WorkflowsTable
                workflows={workflows}
                loading={loading}
                onRowClick={handleRowClick}
                actionsDisabled={isTenantScopeRequired}
                onDeleteSuccess={() => void refetchAgenticWorkflows()}
                onUploadClick={openUploadModal}
            />

            {/* Upload Modal - reusing from AgenticWorkflowDetail */}
            {currentWorkflowForUpload && (
                <UploadContactsModal
                    isOpen={isUploadModalOpen}
                    onClose={closeUploadModal}
                    workflowTitle={currentWorkflowForUpload.title}
                    workflowId={currentWorkflowForUpload.id}
                    onUploadSuccess={() => void refetchAgenticWorkflows()}
                    onNotification={handleUploadNotification}
                />
            )}

            {/* Notification Modal */}
            <NotificationModal
                isOpen={notification.isOpen}
                onClose={closeNotification}
                title={notification.title}
                message={notification.message}
                type={notification.type}
                body={notification.body}
                buttons={notification.buttons}
            />
        </div>
    );
};

export default AgenticWorkflow;
